import { DeliveryProviderPortalService } from '@provider-portal/common/api/openapi_gen/deliveryProviderPortal/deliveryProviderPortal';
import {
  AccountPermissions,
  AccountStage,
  getSelectedProviderData,
  mapSelectedProviderDataToSelectedVendorAndProvider,
  SelectedProvider,
  SelectedVendorAndProvider,
} from '@provider-portal/common/services/types/account';
import { createSelector } from 'reselect';

import { NetworkRequestState } from '@bolteu/redux-session';

import { GlobalAppState } from 'rdx/state';
import ProviderSignupSource = DeliveryProviderPortalService.ProviderSignupSource;
import ProviderLifecycleStatus = DeliveryProviderPortalService.ProviderLifecycleStatus;

export function selectAccountDetails(state: GlobalAppState) {
  return {
    user: {
      email: state.account.details?.account_info?.account_data?.email,
      name: state.account.details?.account_info
        ? `${state.account.details.account_info.account_data.first_name} ${state.account.details.account_info.account_data.last_name}`
        : '',
      avatar: state.account.details?.account_info?.account_data.first_name
        .slice(0, 1)
        .toUpperCase(),
    },
  };
}

export const selectAssignedProviders = (state: GlobalAppState) =>
  state.account.details?.account_info.assignments;

export const selectAccountOnboarding = (state: GlobalAppState) =>
  state.account.details?.account_info.account_data.is_account_onboarding ?? false;

export const selectAssignedProvidersByVendor = (state: GlobalAppState) =>
  state.account.details?.account_info.assignedProvidersByVendor;

export const selectDefaultCurrency = (state: GlobalAppState) =>
  state.account.selectedProvider
    ? state.account.details?.account_info.assignments[state.account.selectedProvider]?.currency
    : undefined;

const selectStoredSelectedProvider = (state: GlobalAppState) => state.account.selectedProvider;

export const selectSelectedProvider = createSelector(
  [selectStoredSelectedProvider, selectAssignedProviders],
  (selectedProvider, assignedProviders): SelectedProvider | null => {
    return getSelectedProviderData(selectedProvider ?? 0, assignedProviders ?? []);
  },
);

export const selectSelectedVendorAndProvider = createSelector(
  [selectSelectedProvider],
  (selectedProvider): SelectedVendorAndProvider | null => {
    return selectedProvider
      ? mapSelectedProviderDataToSelectedVendorAndProvider(selectedProvider)
      : null;
  },
);

export const selectSelectedProviderPermissions = createSelector(
  [selectSelectedProvider],
  (selectedProviderData): AccountPermissions | null => {
    return selectedProviderData?.permissions ?? null;
  },
);

export const selectAssignedProviderIds = createSelector(
  [selectAssignedProviders],
  (assignedProviders) => {
    if (!assignedProviders?.length) {
      return [];
    }

    return assignedProviders.map((provider) => provider.provider_id);
  },
);

export const selectAccountRequestState = (state: GlobalAppState) => {
  return state.account.requestState;
};

/**
 * True only once the account request has succeeded and still returned no venues.
 * The success check is load-bearing: before the request resolves `assignments` is
 * undefined, so a bare length check would be true while the account is still loading.
 */
export const selectIsAccountMisconfigured = createSelector(
  [selectAccountRequestState, selectAssignedProviders],
  (accountRequestState, assignedProviders): boolean =>
    accountRequestState === NetworkRequestState.success && (assignedProviders?.length ?? 0) === 0,
);

export const selectAccountStage = createSelector(
  [selectAccountOnboarding, selectAssignedProviders],
  (accountOnboarding, assignedProviders): AccountStage => {
    if (assignedProviders?.length === 1 && accountOnboarding) {
      return AccountStage.ONBOARDING;
    }
    return AccountStage.ACTIVATED;
  },
);

export const selectIsSelectedProviderInOnboarding = createSelector(
  [selectSelectedProvider],
  (selectedProvider): boolean => {
    return selectedProvider?.lifecycle_status === ProviderLifecycleStatus.ONBOARDING;
  },
);

export const selectIsAccountOrSelectedProviderOnboarding = createSelector(
  [selectAccountStage, selectIsSelectedProviderInOnboarding],
  (accountStage, isSelectedProviderOnboarding): boolean => {
    return accountStage === AccountStage.ONBOARDING || isSelectedProviderOnboarding;
  },
);

// Deliberately narrower than selectIsSelectedProviderInOnboarding: only for gating the
// unconditional menu-editor access that New Merchant Venue onboarding needs.
export const selectIsSelectedProviderNewMerchantVenueInOnboarding = createSelector(
  [selectSelectedProvider],
  (selectedProvider): boolean => {
    return (
      selectedProvider?.signup_source === ProviderSignupSource.NEW_MERCHANT_VENUE &&
      selectedProvider?.lifecycle_status === ProviderLifecycleStatus.ONBOARDING
    );
  },
);

// selectAccountStage assumes a single-provider account; this covers a multi-provider account
// whose currently selected provider is itself still onboarding.
export const selectAccountStageOrNewMerchantVenueOnboarding = createSelector(
  [selectAccountStage, selectIsSelectedProviderInOnboarding],
  (accountStage, isSelectedProviderInOnboarding): AccountStage => {
    if (isSelectedProviderInOnboarding) {
      return AccountStage.ONBOARDING;
    }
    return accountStage;
  },
);
